import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { useEffect } from "react";
import App from "./App.tsx";
import "./index.css";
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundary } from "@/components/system/ErrorBoundary";
import { teardownCrisp } from "@/lib/crisp";

// Nuke any stale Crisp widget left over from previous builds where the
// floating launcher was injected globally. In-app Support still opens Crisp
// on demand via openCrispChat().
if (typeof window !== "undefined") {
  teardownCrisp();

  // Auto-recover from stale hashed chunks after a new deploy. When the
  // browser holds an old index.html referencing a JS chunk that no longer
  // exists, the lazy import() rejects with "Failed to fetch dynamically
  // imported module". Reload once to pick up the fresh index.html.
  const RELOAD_KEY = "__sorvaine_chunk_reload__";
  const isChunkLoadError = (msg: string) =>
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg);

  const tryReload = (msg: string) => {
    if (!isChunkLoadError(msg)) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || "0");
      if (Date.now() - last < 10_000) return; // avoid reload loops
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      /* ignore storage errors */
    }
    window.location.reload();
  };

  window.addEventListener("error", (e) => {
    tryReload(e?.message || "");
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const msg =
      typeof reason === "string" ? reason : reason?.message || String(reason || "");
    tryReload(msg);
  });
}

Sentry.init({
  dsn: "https://cbeb55140c9b641885aed85e7883e9bf@o4511111060979712.ingest.de.sentry.io/4511234189492304",
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: 0.1,
});

// Signals boot completion AFTER React has actually rendered
function BootSignal() {
  useEffect(() => {
    (window as any).__APP_BOOTED__ = true;
  }, []);
  return null;
}

// Handle OAuth / magic-link hash fragments before React Router processes them
// (prevents querySelector crash on "#access_token=..." URLs)
async function bootstrap() {
  const hash = window.location.hash;
  if (hash && hash.includes('access_token') && !hash.includes('type=recovery')) {
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    // Clean the URL immediately so React Router doesn't crash
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', cleanUrl);

    // Await session establishment BEFORE rendering the app
    if (accessToken && refreshToken) {
      await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
    }
  }

  const rootEl = document.getElementById("root");
  if (!rootEl) {
    // DOM not ready yet — defer until DOMContentLoaded
    document.addEventListener('DOMContentLoaded', () => bootstrap(), { once: true });
    return;
  }

  createRoot(rootEl).render(
    <Sentry.ErrorBoundary fallback={<div style={{padding:'40px',textAlign:'center'}}>Something went wrong. Please refresh the page.</div>}>
      <ErrorBoundary>
        <BootSignal />
        <App />
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

bootstrap();
